import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { lookupClientInvite, acceptClientInvite } from './agencyDb';

export default function JoinAgency({ token, onDone, onLogin }) {
  const [invite, setInvite]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [joining, setJoining]   = useState(false);
  const [mode, setMode]         = useState('choose'); // choose | signup | signin
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [name, setName]         = useState('');
  const [done, setDone]         = useState(false);

  useEffect(() => {
    if (token) loadInvite();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function loadInvite() {
    try {
      const data = await lookupClientInvite(supabase, token);
      if (!data) { setError('This invite link is invalid or has expired.'); setLoading(false); return; }
      if (data.invite_accepted_at) { setError('This invite has already been accepted.'); setLoading(false); return; }
      setInvite(data);
    } catch (e) {
      setError('Failed to load invite: ' + e.message);
    }
    setLoading(false);
  }

  async function handleSignUp(e) {
    e.preventDefault();
    setJoining(true);
    setError('');
    try {
      const { data: authData, error: signupErr } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { company_name: invite.company_name || name } },
      });
      if (signupErr) throw signupErr;
      await acceptClientInvite(supabase, token, authData.user.id);
      setDone(true);
    } catch (e) {
      setError(e.message);
    }
    setJoining(false);
  }

  async function handleSignIn(e) {
    e.preventDefault();
    setJoining(true);
    setError('');
    try {
      const { data: authData, error: signinErr } = await supabase.auth.signInWithPassword({ email, password });
      if (signinErr) throw signinErr;
      await acceptClientInvite(supabase, token, authData.user.id);
      setDone(true);
      if (onDone) onDone();
    } catch (e) {
      setError(e.message);
    }
    setJoining(false);
  }

  // ── Theming from agency white-label ────────────────────────────────────────
  const agency = invite?.agencies;
  const accentColor = agency?.white_label_color || '#2D7DD2';
  const agencyName  = agency?.white_label_enabled && agency?.white_label_name
    ? agency.white_label_name
    : (invite?.agencies?.agency_name || 'Your Consultant');

  // ── Render helpers ──────────────────────────────────────────────────────────
  const containerStyle = {
    minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center',
    justifyContent: 'center', padding: 24,
  };
  const cardStyle = {
    background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 16, padding: '40px 36px', width: '100%', maxWidth: 440,
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
  };
  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 8, fontSize: 14,
    background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)',
    outline: 'none', boxSizing: 'border-box',
  };
  const btnStyle = {
    width: '100%', padding: '12px 0', borderRadius: 8, fontSize: 14, fontWeight: 600,
    cursor: 'pointer', border: 'none', color: '#fff',
    background: accentColor, transition: 'opacity 0.15s',
  };

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={containerStyle}>
      <div style={{ color: 'var(--text3)', fontSize: 14 }}>Loading invite…</div>
    </div>
  );

  // ── Error (bad token) ───────────────────────────────────────────────────────
  if (error && !invite) return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 52, height: 52, borderRadius: '50%', margin: '0 auto 16px',
            background: 'var(--red-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Invalid Invite</div>
          <div style={{ color: 'var(--text3)', fontSize: 14, marginBottom: 24 }}>{error}</div>
          <button onClick={() => onLogin && onLogin()} style={{ ...btnStyle, background: 'var(--accent)' }}>
            Sign In Instead
          </button>
        </div>
      </div>
    </div>
  );

  // ── Done ────────────────────────────────────────────────────────────────────
  if (done) return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 52, height: 52, borderRadius: '50%', margin: '0 auto 16px',
            background: 'rgba(29,184,122,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>You're in!</div>
          <div style={{ color: 'var(--text3)', fontSize: 14, marginBottom: 24 }}>
            Your account is linked to {agencyName}. Check your email to confirm, then log in to TheClearAudit.
          </div>
          <button onClick={() => onLogin && onLogin()} style={{ ...btnStyle, background: accentColor }}>
            Go to Login
          </button>
        </div>
      </div>
    </div>
  );

  // ── Choose mode ─────────────────────────────────────────────────────────────
  if (mode === 'choose') return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        {/* Agency branding */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          {agency?.white_label_logo_url ? (
            <img src={agency.white_label_logo_url} alt="Agency Logo" style={{ height: 44, marginBottom: 12, borderRadius: 8 }} />
          ) : (
            <div style={{
              width: 52, height: 52, borderRadius: 14, margin: '0 auto 12px',
              background: accentColor, display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: 22, fontWeight: 800, color: '#fff',
            }}>
              {agencyName.charAt(0).toUpperCase()}
            </div>
          )}
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>Invited by</div>
          <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{agencyName}</div>
          <div style={{ fontSize: 14, color: 'var(--text3)' }}>
            You've been invited to join the TheClearAudit compliance platform
            {invite?.company_name ? ` for ${invite.company_name}` : ''}.
          </div>
        </div>

        {/* Invite card */}
        <div style={{
          background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 10,
          padding: '14px 16px', marginBottom: 24,
        }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div style={{
              width: 36, height: 36, borderRadius: 8, background: 'rgba(45,125,210,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>
                {invite.client_name || invite.client_email}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>{invite.client_email}</div>
              {invite.company_name && (
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                  {invite.company_name}{invite.cage_code ? ` · CAGE: ${invite.cage_code}` : ''}
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button
            onClick={() => setMode('signup')}
            style={{ ...btnStyle, background: accentColor }}
          >
            Create New Account
          </button>
          <button
            onClick={() => setMode('signin')}
            style={{ ...btnStyle, background: 'var(--surface2)', color: 'var(--text)', border: '1px solid var(--border)' }}
          >
            Sign In to Existing Account
          </button>
        </div>

        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: 'var(--text3)' }}>
          By continuing, you agree to TheClearAudit's{' '}
          <span style={{ color: 'var(--accent)', cursor: 'pointer' }}>Terms of Service</span>{' '}
          and{' '}
          <span style={{ color: 'var(--accent)', cursor: 'pointer' }}>Privacy Policy</span>.
        </div>
      </div>
    </div>
  );

  // ── Sign Up form ─────────────────────────────────────────────────────────────
  if (mode === 'signup') return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <button
          onClick={() => setMode('choose')}
          style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 13, marginBottom: 16, padding: 0 }}
        >
          ← Back
        </button>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Create Account</div>
        <div style={{ fontSize: 14, color: 'var(--text3)', marginBottom: 24 }}>
          Joining as part of {invite?.company_name || 'your organization'}
        </div>
        {error && (
          <div style={{ background: 'var(--red-bg)', border: '1px solid var(--red)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--red)', marginBottom: 16 }}>
            {error}
          </div>
        )}
        <form onSubmit={handleSignUp} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 5 }}>Company Name</label>
            <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} placeholder={invite?.company_name || 'Your company'} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 5 }}>Email</label>
            <input style={inputStyle} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={invite?.client_email || 'you@company.com'} required />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 5 }}>Password</label>
            <input style={inputStyle} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min 8 characters" minLength={8} required />
          </div>
          <button type="submit" disabled={joining} style={{ ...btnStyle, background: accentColor, opacity: joining ? 0.7 : 1 }}>
            {joining ? 'Creating Account…' : 'Create Account & Accept Invite'}
          </button>
        </form>
      </div>
    </div>
  );

  // ── Sign In form ─────────────────────────────────────────────────────────────
  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <button
          onClick={() => setMode('choose')}
          style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer', fontSize: 13, marginBottom: 16, padding: 0 }}
        >
          ← Back
        </button>
        <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Sign In</div>
        <div style={{ fontSize: 14, color: 'var(--text3)', marginBottom: 24 }}>
          Link your existing account to accept this invite
        </div>
        {error && (
          <div style={{ background: 'var(--red-bg)', border: '1px solid var(--red)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--red)', marginBottom: 16 }}>
            {error}
          </div>
        )}
        <form onSubmit={handleSignIn} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 5 }}>Email</label>
            <input style={inputStyle} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" required />
          </div>
          <div>
            <label style={{ fontSize: 12, color: 'var(--text3)', display: 'block', marginBottom: 5 }}>Password</label>
            <input style={inputStyle} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Your password" required />
          </div>
          <button type="submit" disabled={joining} style={{ ...btnStyle, background: accentColor, opacity: joining ? 0.7 : 1 }}>
            {joining ? 'Signing In…' : 'Sign In & Accept Invite'}
          </button>
        </form>
      </div>
    </div>
  );
}
